import { CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import React from 'react';

import DisabledSecureIcon from '../../../../assets/icons/header/checkout/disabledSecure.svg';
import SecureIcon from '../../../../assets/icons/header/checkout/secure.svg';
import HelpIcon from '../../../../assets/icons/helpToolTip/helptoolTip.svg';
import CardIcon from '../../../../assets/icons/payment-input-form/card.svg';
import Button from '../../../../shared/components/Button';
import Tooltip from '../../../../shared/components/Tooltip';
import { PAYMENT_TEXTS } from '../../constants/payment.constants';
import PaymentInputGroup from '../shared/PaymentInputGroup';
import PaymentMethodItem from '../shared/PaymentMethodItem';
import PaymentTerms from '../shared/PaymentTerms';

import { CARD_ICONS, MOBILE_CARD_ICONS, STRIPE_ELEMENT_OPTIONS, STRIPE_FIELDS } from './StripePayment.constants';
import { useStripePayment } from './hooks/useStripePayment';

export interface StripePaymentFormProps {
    clientSecret: string | null;
    onCreatePaymentIntent?: () => Promise<{ clientSecret: string } | null>;
    onPaymentSuccess?: (paymentIntent: import('@stripe/stripe-js').PaymentIntent) => void;
    onPaymentError?: (error: import('@stripe/stripe-js').StripeError | Error | string | unknown) => void;
    expanded: boolean;
    onToggle?: () => void;
    totalAmount?: number | null;
    currencyCode?: string;
    termsAccepted: boolean;
    setTermsAccepted: (accepted: boolean) => void;
}

const StripePaymentForm: React.FC<StripePaymentFormProps> = ({
    clientSecret,
    onCreatePaymentIntent,
    onPaymentSuccess,
    onPaymentError,
    expanded,
    onToggle,
    totalAmount,
    currencyCode,
    termsAccepted,
    setTermsAccepted,
}) => {
    const {
        formData,
        errors,
        isProcessing,
        nameInputRef,
        handleNameChange,
        handlePayNow,
        setStripeElementStatus,
        isFormValid,
    } = useStripePayment({
        clientSecret,
        onCreatePaymentIntent,
        onPaymentSuccess,
        onPaymentError,
        termsAccepted,
    });

    return (
        <>
            {/* Credit Card Section */}
            <PaymentMethodItem
                expanded={expanded}
                onToggle={onToggle}
                className="w-[588px] max-[576px]:w-full max-[576px]:max-w-[350px] max-[576px]:mx-auto flex flex-col gap-[20px]"
            >
                <div className="flex items-start justify-between h-[40px] max-[576px]:h-auto">
                    <div className="max-[576px]:w-full">
                        <div className="flex justify-between items-start w-full">
                            <h3 className="font-noto font-bold text-[14px] leading-[20px] text-[#030407] m-0 p-0">{PAYMENT_TEXTS.CARD.TITLE}</h3>
                            {/* Mobile Icon */}
                            <img
                                src={CardIcon}
                                alt="Card"
                                className="hidden max-[576px]:block w-[20px] h-[20px] object-contain brightness-0"
                            />
                        </div>

                        <p
                            className="font-normal text-[12px] leading-[18px] text-[#667085] m-0 p-0 max-[576px]:mt-1 max-[576px]:mb-[16px]"
                            style={{ fontFamily: 'Nano Sans, sans-serif' }}
                        >
                            <span className="max-[576px]:hidden">{PAYMENT_TEXTS.CARD.SUBTITLE_DESKTOP}</span>
                            <span className="hidden max-[576px]:inline">{PAYMENT_TEXTS.CARD.SUBTITLE_MOBILE}</span>
                        </p>
                    </div>

                    {/* Card Icons - Desktop */}
                    <div className="flex gap-2 max-[576px]:hidden transition-opacity duration-200">
                        {CARD_ICONS.map((icon, index) => (
                            <img
                                key={index}
                                src={icon.src}
                                alt={icon.alt}
                                className="w-[23px] h-[16px] object-contain"
                            />
                        ))}
                    </div>
                </div>

                {expanded && (
                    <>
                        <hr className="border-t-[1px] border-[#D0D5DD] m-0" />

                        <div className="flex flex-col gap-[12px]">
                            {/* Name on Card */}
                            <PaymentInputGroup label="Name on card" htmlFor="nameOnCard" error={errors.nameOnCard}>
                                <input
                                    id="nameOnCard"
                                    ref={nameInputRef}
                                    type="text"
                                    name="nameOnCard"
                                    value={formData.nameOnCard}
                                    onChange={handleNameChange}
                                    placeholder={PAYMENT_TEXTS.CARD.NAME_PLACEHOLDER}
                                    className={`w-full px-4 h-[40px] border rounded-[6px] focus:outline-none focus:border-[#030407] font-['Nano_Sans',sans-serif] font-normal ${errors.nameOnCard ? 'border-red-500' : 'border-[#D0D5DD]'}`}
                                />
                            </PaymentInputGroup>

                            {/* Card Number - Stripe Element */}
                            <PaymentInputGroup label="Card number" htmlFor={STRIPE_FIELDS.CARD_NUMBER} error={errors.cardNumber}>
                                <div className="border rounded-[6px] border-[#D0D5DD] bg-white gap-2 pr-2 min-h-[40px] flex items-center">
                                    <div className="flex-1 py-[10px] px-[16px]">
                                        <CardNumberElement
                                            id={STRIPE_FIELDS.CARD_NUMBER}
                                            options={STRIPE_ELEMENT_OPTIONS}
                                            onChange={(e) => setStripeElementStatus(prev => ({ ...prev, cardNumber: e.complete }))}
                                        />
                                    </div>
                                    {/* Mobile Card Icons inside Input */}
                                    <div className="hidden max-[576px]:flex items-center gap-1">
                                        {MOBILE_CARD_ICONS.map((icon) => (
                                            <img key={icon.alt} src={icon.src} alt={icon.alt} className="w-[23px] h-[15px] object-contain" />
                                        ))}
                                    </div>
                                </div>
                            </PaymentInputGroup>

                            {/* Expiry Date and Security Code - Stripe Elements */}
                            <div className="grid grid-cols-2 gap-4">
                                <PaymentInputGroup label="Expiry date" htmlFor={STRIPE_FIELDS.EXPIRY_DATE} error={errors.expiryDate}>
                                    <div className="border rounded-[6px] border-[#D0D5DD] bg-white min-h-[40px] flex items-center">
                                        <div className="w-full py-[10px] px-[16px]">
                                            <CardExpiryElement
                                                id={STRIPE_FIELDS.EXPIRY_DATE}
                                                options={STRIPE_ELEMENT_OPTIONS}
                                                onChange={(e) => setStripeElementStatus(prev => ({ ...prev, cardExpiry: e.complete }))}
                                            />
                                        </div>
                                    </div>
                                </PaymentInputGroup>

                                <div className="relative">
                                    <PaymentInputGroup label="Security code" htmlFor={STRIPE_FIELDS.SECURITY_CODE} error={errors.securityCode}>
                                        <div className="relative">
                                            <div className="border rounded-[6px] border-[#D0D5DD] bg-white min-h-[40px] flex items-center">
                                                <div className="w-full py-[10px] px-[16px]">
                                                    <CardCvcElement
                                                        id={STRIPE_FIELDS.SECURITY_CODE}
                                                        options={STRIPE_ELEMENT_OPTIONS}
                                                        onChange={(e) => setStripeElementStatus(prev => ({ ...prev, cardCvc: e.complete }))}
                                                    />
                                                </div>
                                            </div>
                                            {/* Icons inside input field */}
                                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                                                <img
                                                    src={CardIcon}
                                                    alt="Card"
                                                    className="w-5 h-5"
                                                />
                                                <Tooltip content={PAYMENT_TEXTS.CARD.CVV_TOOLTIP}>
                                                    <img
                                                        src={HelpIcon}
                                                        alt="Help"
                                                        className="w-5 h-5 cursor-pointer"
                                                    />
                                                </Tooltip>
                                            </div>
                                        </div>
                                    </PaymentInputGroup>
                                </div>
                            </div>
                        </div>

                        {/* Terms & Conditions */}
                        <PaymentTerms
                            termsAccepted={termsAccepted}
                            setTermsAccepted={setTermsAccepted}
                            id="terms-checkbox"
                        />

                        {/* Info Message - Only shown when Pay button is enabled */}
                        {isFormValid && (
                            <div className="w-[548px] max-[576px]:w-full h-[42px] max-[576px]:h-auto rounded-[8px] bg-[#F9FAFB] p-[12px] flex items-center justify-center gap-[24px]">
                                <p className="w-[524px] max-[576px]:w-full h-[18px] max-[576px]:h-auto font-noto font-normal text-[12px] leading-[18px] text-[#667085] text-center m-0 p-0">
                                    {PAYMENT_TEXTS.CARD.TEMP_HOLD_MESSAGE}
                                </p>
                            </div>
                        )}

                        {/* Pay Now Button */}
                        <Button
                            variant="primary"
                            onClick={handlePayNow}
                            disabled={!isFormValid || isProcessing}
                            className="w-full h-[48px] px-[20px] rounded-[6px] flex items-center justify-center gap-[6px] font-noto !font-bold !text-[14px] leading-[20px] disabled:bg-[#EAECF0] disabled:!text-[#667085] disabled:opacity-100 disabled:cursor-not-allowed bg-[#030407] text-white hover:bg-black"
                        >
                            <img
                                src={(!isFormValid || isProcessing) ? DisabledSecureIcon : SecureIcon}
                                alt="Secure"
                                className={`w-[20px] h-[20px] ${(!isFormValid || isProcessing) ? '' : 'brightness-0 invert'}`}
                            />
                            {/* Desktop: Show only "Pay now" */}
                            <span className="hidden min-[576px]:inline">
                                {isProcessing ? 'Processing...' : 'Pay now'}
                            </span>
                            {/* Mobile: Show "Pay now (amount)" */}
                            <span className="inline min-[576px]:hidden">
                                {isProcessing ? 'Processing...' : `Pay now${totalAmount && currencyCode ? ` (${new Intl.NumberFormat('en', { style: 'currency', currency: currencyCode }).format(totalAmount)})` : ''}`}
                            </span>
                        </Button>

                        {errors.card && (
                            <p className="text-red-500 text-sm mt-4 text-center">{errors.card}</p>
                        )}
                    </>
                )}
            </PaymentMethodItem>
        </>
    );
};
export default StripePaymentForm;
